import * as XLSX from "xlsx";
import { downloadBlob } from "./downloadBlob";
import { CurateActivityDto, CurateResourceListItemDto } from "../../Types/Curate/CurateResource";

export type CurateExportRow = {
  title: string;
  type: string;
  url?: string;
  parent?: string;
  grants: number;
  recommendations: number;
  clicks: number;
  summaryStatus?: string;
  active: boolean;
};

const ctrLabel = (recommendations: number, clicks: number): string =>
  recommendations <= 0 ? "" : `${Math.round((clicks / recommendations) * 100)}%`;

const resourceType = (resource: CurateResourceListItemDto): string => {
  if (resource.parentId) return "Page";
  if (resource.kind === "source" || resource.kind === "link") return "URL";
  return (resource.extension || resource.kind || "File").replace(/^\./, "").toUpperCase();
};

export const toCurateExportRow = (
  resource: CurateResourceListItemDto,
  parentTitle?: string
): CurateExportRow => ({
  title: resource.title || "Untitled",
  type: resourceType(resource),
  url: resource.url,
  parent: parentTitle,
  grants: resource.grantCount ?? 0,
  recommendations: resource.recommendationCount ?? 0,
  clicks: resource.clickCount ?? 0,
  summaryStatus: resource.summaryStatus,
  active: resource.isActive !== false,
});

export function downloadCurateEngagementWorkbook(args: {
  filename: string;
  resources: CurateExportRow[];
  activity?: CurateActivityDto[];
}): void {
  const wb = XLSX.utils.book_new();

  const resourceSheet = XLSX.utils.aoa_to_sheet([
    [
      "Title",
      "Type",
      "URL",
      "Parent",
      "Clients with access",
      "Recommendations",
      "Clicks",
      "CTR",
      "Summary status",
      "Active",
    ],
    ...args.resources.map((row) => [
      row.title,
      row.type,
      row.url ?? "",
      row.parent ?? "",
      row.grants,
      row.recommendations,
      row.clicks,
      ctrLabel(row.recommendations, row.clicks),
      row.summaryStatus ?? "",
      row.active ? "Yes" : "No",
    ]),
  ]);
  XLSX.utils.book_append_sheet(wb, resourceSheet, "Resources");

  const activity = args.activity ?? [];
  if (activity.length > 0) {
    const historySheet = XLSX.utils.aoa_to_sheet([
      ["When (UTC)", "Type", "User", "Organisation", "Resource", "URL"],
      ...activity.map((event) => [
        event.timestamp ?? "",
        event.eventType ?? "",
        event.userName || event.userSub || "",
        event.organisationName || event.clientId || "",
        event.resourceTitle ?? "",
        event.url ?? "",
      ]),
    ]);
    XLSX.utils.book_append_sheet(wb, historySheet, "History");
  }

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
  downloadBlob(
    new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    args.filename
  );
}

export const curateExportFilename = (label: string): string => {
  const safe = (label || "curate")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  const day = new Date().toISOString().slice(0, 10);
  return `curate-engagement-${safe || "export"}-${day}.xlsx`;
};
